/** Users and roles. */

import { useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '@/lib/api';
import type {
  AssignRoleRequest,
  CreateUserRequest,
  Paged,
  RoleKey,
  UserResponse,
} from '@/lib/schemas';
import { Badge, Button, Card, ErrorState, Loading, Table } from '@/components/ui';
import { useCan } from '@/hooks/usePermissions';

/** The six roles, in the order the permission matrix documents them. */
const ROLES = ['owner', 'admin', 'analyst', 'reviewer', 'viewer', 'auditor'] as const;

/** The admin view. */
export function AdminPage(): ReactElement {
  const canManage = useCan('manage_users_and_roles');
  const queryClient = useQueryClient();

  const users = useQuery({
    queryKey: ['users'],
    queryFn: () => request<Paged<UserResponse>>('/users', { query: { limit: 100 } }),
    enabled: canManage,
  });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['users'] });
  };

  const assign = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: RoleKey }) =>
      request<void, AssignRoleRequest>(`/users/${userId}/roles`, { method: 'POST', body: { role } }),
    onSuccess: refresh,
  });

  const revoke = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      request<void>(`/users/${userId}/roles/${role}`, { method: 'DELETE' }),
    onSuccess: refresh,
  });

  // Two mutations rather than one with a computed verb: `/users/${id}:${verb}` is a path
  // no static check can match, and a reader has to evaluate a ternary to learn which
  // endpoint is called.
  const suspend = useMutation({
    mutationFn: (userId: string) =>
      request<UserResponse>(`/users/${userId}:suspend`, { method: 'POST' }),
    onSuccess: refresh,
  });

  const activate = useMutation({
    mutationFn: (userId: string) =>
      request<UserResponse>(`/users/${userId}:activate`, { method: 'POST' }),
    onSuccess: refresh,
  });

  if (!canManage) {
    return (
      <p className="text-sm text-muted">
        Managing users requires the <code>manage_users_and_roles</code> permission, which your
        role does not hold.
      </p>
    );
  }
  if (users.isLoading) return <Loading />;
  if (users.error) return <ErrorState error={users.error} />;

  const failure = assign.error ?? revoke.error ?? suspend.error ?? activate.error;

  // Mirror the server's last-manager invariant (ADR-0045) so the interface never offers a
  // control that would lock the tenant out of user administration. The server still
  // refuses it; a disabled control with a reason simply beats a 403 toast after the click.
  const userList = users.data?.data ?? [];
  const isManagementRole = (role: string): boolean => role === 'owner' || role === 'admin';
  const activeManagerIds = userList
    .filter((u) => u.status === 'active' && u.roles.some(isManagementRole))
    .map((u) => u.id);
  const lastManagerId = activeManagerIds.length === 1 ? activeManagerIds[0] : null;

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold">Users</h1>
      <p className="text-sm text-muted">
        Roles are additive, and no role approves its own work — an analyst cannot approve
        anything, an admin cannot approve merges or exports. Accounts are suspended rather
        than deleted: every audit entry names its actor, so a removed user would leave
        entries pointing at nobody.
      </p>

      {failure ? <ErrorState error={failure} /> : null}

      <CreateUserForm onCreated={refresh} />

      <Table
        headers={['Email', 'Name', 'Roles', 'MFA', 'Status', '']}
        empty="No users."
        rows={userList.map((user) => {
          const isLastManager = user.id === lastManagerId;
          const managementRolesHeld = user.roles.filter(isManagementRole);
          return [
          <span key="e" className="font-mono text-xs">
            {user.email}
          </span>,
          user.display_name,
          <span key="r" className="flex flex-wrap items-center gap-1">
            {user.roles.map((role) => {
              // Revoking this role would strand the tenant only if it is a management role,
              // its holder is the last active manager, and it is the last management role
              // they hold (a colleague with both owner and admin keeps one on revocation).
              const wouldStrandTenant =
                isLastManager && isManagementRole(role) && managementRolesHeld.length === 1;
              return (
                <button
                  key={role}
                  type="button"
                  className="disabled:cursor-not-allowed disabled:opacity-50"
                  title={
                    wouldStrandTenant
                      ? `Cannot revoke ${role}: this is the last account that can manage users. Grant another owner or admin first.`
                      : `Revoke ${role} from ${user.email}`
                  }
                  onClick={() => revoke.mutate({ userId: user.id, role })}
                  disabled={revoke.isPending || wouldStrandTenant}
                >
                  <Badge>{role} ✕</Badge>
                </button>
              );
            })}
            <select
              aria-label={`Grant a role to ${user.email}`}
              className="rounded border border-line bg-raised px-1 text-xs"
              value=""
              onChange={(event) => {
                if (event.target.value) {
                  // The DOM hands back a string; the options are `ROLES`, which the
                  // schema types. This is the boundary where that has to be said.
                  assign.mutate({ userId: user.id, role: event.target.value as RoleKey });
                }
              }}
            >
              <option value="">+ role</option>
              {ROLES.filter((role) => !user.roles.includes(role)).map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
          </span>,
          <Badge key="m" tone={user.mfa_enabled ? 'ok' : 'warn'}>
            {user.mfa_enabled ? 'on' : 'off'}
          </Badge>,
          <Badge key="s" tone={user.status === 'active' ? 'ok' : 'muted'}>
            {user.status}
          </Badge>,
          user.status === 'active' ? (
            <Button
              key="a"
              onClick={() => suspend.mutate(user.id)}
              disabled={suspend.isPending || isLastManager}
              title={
                isLastManager
                  ? 'This is the last active account that can manage users. Grant another owner or admin first.'
                  : undefined
              }
            >
              Suspend
            </Button>
          ) : (
            <Button key="a" onClick={() => activate.mutate(user.id)} disabled={activate.isPending}>
              Reinstate
            </Button>
          ),
          ];
        })}
      />
    </div>
  );
}

/**
 * Create an account.
 *
 * The administrator types the initial password and hands it over out of band. There is no
 * mail transport in this platform, so there is no invitation to send — and a "send invite"
 * button that silently did nothing would be worse than saying so.
 */
function CreateUserForm({ onCreated }: { onCreated: () => void }): ReactElement {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<RoleKey>('analyst');

  const create = useMutation({
    // One call, not two: `POST /users` takes the roles and grants them in the same
    // transaction. Creating first and granting afterwards would leave a window where the
    // account exists with no roles — able to sign in and do nothing.
    mutationFn: () =>
      request<{ id: string }, CreateUserRequest>('/users', {
        method: 'POST',
        body: { email, display_name: displayName, password, roles: [role] },
      }),
    onSuccess: () => {
      setEmail('');
      setDisplayName('');
      setPassword('');
      onCreated();
    },
  });

  return (
    <Card title="Add a user">
      <div className="space-y-2 p-4">
        {create.error ? <ErrorState error={create.error} /> : null}
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col text-xs">
            Email
            <input
              className="rounded border border-line bg-raised px-2 py-1 text-xs"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="analyst@example.org"
            />
          </label>
          <label className="flex flex-col text-xs">
            Display name
            <input
              className="rounded border border-line bg-raised px-2 py-1 text-xs"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              placeholder="A. Analyst"
            />
          </label>
          <label className="flex flex-col text-xs">
            Initial password
            <input
              className="rounded border border-line bg-raised px-2 py-1 text-xs"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </label>
          <label className="flex flex-col text-xs">
            Role
            <select
              className="rounded border border-line bg-raised px-2 py-1 text-xs"
              value={role}
              onChange={(event) => setRole(event.target.value as RoleKey)}
            >
              {ROLES.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <Button
            onClick={() => create.mutate()}
            disabled={!email || !displayName || !password || create.isPending}
          >
            Create
          </Button>
        </div>
        <p className="text-xs text-muted">
          Hand the password over out of band and have them enable MFA before they work a
          case. This platform has no email delivery and no password reset.
        </p>
      </div>
    </Card>
  );
}
